// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginUser,
  registerUser,
  forgotpassword,
  updatePassword,
} from "../../API/axiosClient";

// Utility function to get error message
// const getErrorMessage = (error) => {
//   if (error.response && error.response.data) {
//     const { error: isError, message } = error.response.data;

//     // Check if error is true and return the specific message
//     if (isError) {
//       return message || "An unknown error occurred";
//     }
//   }

//   return "An unknown error occurred";  // Default fallback message
// };

const getErrorMessage = (error) => {
  if (error.response && error.response.data) {
    return error.response.data;
  }
  return { message: "Invalid login credentials" };
};

export const login = createAsyncThunk(
  "auth/login",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await loginUser(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Async thunk for handling registration
export const register = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await registerUser(userData);
      return response.data; // Adjust based on actual response structure
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Async thunk for handling forgot password
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotpassword({ email });
      return response.data; // Adjust based on actual response structure
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Async thunk for handling password update
// export const updatePasswordThunk = createAsyncThunk(
//   'auth/updatePassword',
//   async ({ new_password, token }, { rejectWithValue }) => {
//     try {
//       const response = await updatePassword({ new_password, token });
//       return response.data; // Adjust based on actual response structure
//     } catch (error) {
//       return rejectWithValue(getErrorMessage(error));
//     }
//   }
// );

export const updatePasswordThunk = createAsyncThunk(
  "auth/updatePassword",
  async ({ new_password, confirm_password, token }, thunkAPI) => {
    try {
      const response = await updatePassword("/api/update-password", {
        new_password,
        confirm_password,
        token,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    status: "idle",
    error: null,
    isLoggedIn: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoggedIn = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isLoggedIn = false;
      });

    // Handle registration actions
    builder
      .addCase(register.pending, (state) => {
        state.status = "loading";
        state.error = null; // Clear error on new request
      })
      .addCase(register.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload; // Save user data
      })
      .addCase(register.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload; // Store error data
      });

    // Handle forgot password actions
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.status = "loading";
        state.error = null; // Clear error on new request
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload; // Store error data
      });

    // Handle update password actions
    builder
      .addCase(updatePasswordThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updatePasswordThunk.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updatePasswordThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
